// Force direct downloads for brand platform download links (except PDFs).
// Fetching the file as a blob and triggering a synthetic download bypasses
// cross-origin issues where the browser would otherwise open the asset inline.

class BpDownload {
    constructor () {
        const defaults = {
            initAttr: 'a[download]',
            brandClass: 'is--brand',
            // links with their own download handling must not be intercepted here
            skipSelector: '[data-basket-download-all]',
            fallbackName: 'download'
        };

        this.settings = defaults;
        this.$body = document.body;
    }

    initialize () {
        // scope to the brand platform only – the main site shares this bundle
        if (!this.$body.classList.contains(this.settings.brandClass)) {
            return;
        }

        this.setEvents();
    }

    setEvents () {
        // single delegated listener also covers AJAX-loaded results
        // (basket / asset search) that never dispatch "content.loaded"
        this.$body.addEventListener('click', (e) => {
            const $link = e.target.closest(this.settings.initAttr);

            if (!$link || !this.$body.contains($link)) {
                return;
            }

            if ($link.matches(this.settings.skipSelector)) {
                return;
            }

            const href = $link.getAttribute('href');

            // ignore placeholder / non-file links, and blob URLs: the synthetic
            // download anchor (here and e.g. in basket.js) uses a blob href and
            // its programmatic click bubbles back here – skipping it prevents an
            // infinite download loop.
            if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('blob:')) {
                return;
            }

            // let the browser handle PDFs natively
            if (this.isPdf($link.href)) {
                return;
            }

            e.preventDefault();
            this.download($link);
        });
    }

    async download ($link) {
        try {
            const response = await fetch($link.href);

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const $anchor = document.createElement('a');

            $anchor.style.display = 'none';
            $anchor.href = url;
            $anchor.download = this.getFilename($link, response);
            document.body.appendChild($anchor);
            $anchor.click();

            document.body.removeChild($anchor);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.warn('Direct download failed, falling back to link:', error);
            // best effort fallback so the user still receives the file
            window.open($link.href, '_blank', 'noopener');
        }
    }

    // check the URL path (query strings excluded) for a .pdf extension
    isPdf (href) {
        try {
            return new URL(href, window.location.origin).pathname.toLowerCase().endsWith('.pdf');
        } catch {
            return href.toLowerCase().split('?')[0].endsWith('.pdf');
        }
    }

    // derive a sensible file name: download attribute -> content-disposition -> URL segment
    getFilename ($link, response) {
        const attr = $link.getAttribute('download');
        if (attr) {
            return attr;
        }

        const disposition = response.headers.get('content-disposition');
        if (disposition) {
            const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
            if (utf8Match) {
                return decodeURIComponent(utf8Match[1].trim().replace(/['"]/g, ''));
            }

            const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
            if (asciiMatch) {
                return asciiMatch[1].trim();
            }
        }

        try {
            const segment = new URL($link.href, window.location.origin).pathname.split('/').filter(Boolean).pop();
            if (segment) {
                return decodeURIComponent(segment);
            }
        } catch {
            // ignore malformed URLs and use the fallback below
        }

        return this.settings.fallbackName;
    }
}

export { BpDownload };

const bpDownload = new BpDownload();
bpDownload.initialize();
